import React, { useState } from 'react';
import { I18n } from '@aws-amplify/core';
import Select from '@/components/ui/Select';
import { Button, Divider, Input } from 'antd';

import { useLine } from '../hooks';
import FormItem from '@/components/ui/FormItem';
import { useCreateLine } from '@/hooks/v1/useLineMutations';
import { useApplicationContext } from '@/context/v1/Application/context';

interface LineFormItemProps {
	fieldName?: string | string[];
	required?: boolean;
}

export function LineFormItem({ required = true, fieldName }: Readonly<LineFormItemProps>): JSX.Element {
	const [lineName, setLineName] = useState<string>('');

	const { organization, company } = useApplicationContext();
	const { isLoading: isCreateLineLoading, mutateAsync: createLine } = useCreateLine();
	const { items, isFetching, isDisabled, lineId, sectorId, handleClearLines, handleSelectLine } = useLine({
		fieldName
	});

	async function handleOnCreateLine(): Promise<void> {
		const name = lineName.trim();

		if (!name || !sectorId) return;

		const payload = {
			organization_id: organization.id!,
			company_id: company.id!,
			sector_id: sectorId,
			name
		};

		const result = await createLine({ body: payload });

		const newId = result?.data?.id;

		if (newId) {
			handleSelectLine(newId);
		}

		setLineName('');
	}

	return (
		<FormItem label="Line" name={fieldName} requiredMessage="The field requires an answer" required={required}>
			<Select
				allowClear
				showSearch
				value={lineId}
				options={items}
				loading={isFetching}
				disabled={isDisabled}
				onClear={handleClearLines}
				onChange={handleSelectLine}
				placeholder="Line"
				dropdownRender={(menu) => (
					<>
						{menu}
						<Divider style={{ margin: '8px 0' }} />
						<div style={{ padding: '0 8px 4px', display: 'flex', gap: 8 }}>
							<Input
								value={lineName}
								onChange={(e) => setLineName(e.target.value)}
								placeholder={I18n.get('New line name')}
								style={{ flex: 1 }}
							/>
							<Button
								type="text"
								onClick={handleOnCreateLine}
								loading={isCreateLineLoading}
								disabled={!sectorId || !lineName.trim() || lineName.trim().length < 2}
							>
								{I18n.get('Add')}
							</Button>
						</div>
					</>
				)}
			/>
		</FormItem>
	);
}
